import {
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Link,
  Text
} from "@modernatx/ui-kit-react";
import React from "react";

import { InvoicePriceSummary } from "@/types/Invoice";
import { formatCurrency, formatTimestampDate } from "@/utils/InvoicePay";

export interface PaymentSummaryCardProps {
  invoicePriceSummary: InvoicePriceSummary;
  onPayNow?: () => void;
  isPaymentMethodSelected: boolean;
  isPaymentFormOpen?: boolean;
}

const CheckboxLabel: React.FC = () => {
  return (
    <Box sx={{ pt: "spacing-7" }}>
      <Text>
        I agree to Moderna&apos;s{" "}
        <Link target="_blank" href="/terms-of-use">
          Conditions of Use
        </Link>
        ,
        <Link target="_blank" href="/privacy-policy">
          {" "}
          Privacy Policy
        </Link>
        , and
        <Link target="_blank" href="/terms-and-conditions-of-sale">
          {" "}
          Terms and Conditions of Sale.
        </Link>
      </Text>
    </Box>
  );
};

export const PaymentSummaryCard: React.FC<PaymentSummaryCardProps> = ({
  invoicePriceSummary,
  onPayNow,
  isPaymentMethodSelected,
  isPaymentFormOpen
}) => {
  const [isChecked, setIsChecked] = React.useState<boolean>(false);

  return (
    <Card>
      <Box
        sx={{
          display: "flex",
          gap: "spacing-4",
          padding: "spacing-6",
          flexDirection: "column",
          width: "100%"
        }}
      >
        <Text variant="h6">Summary</Text>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pb: "spacing-4"
            }}
          >
            <Text weight="medium">
              Subtotal ({invoicePriceSummary.totalDoses} doses):
            </Text>
            <Text weight="medium">
              {formatCurrency(invoicePriceSummary.subtotal)}
            </Text>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pb: "spacing-4"
            }}
          >
            <Text>Shipping:</Text>
            <Text>{formatCurrency(0)}</Text>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pb: "spacing-4"
            }}
          >
            <Text>Sales tax:</Text>
            <Text>{formatCurrency(invoicePriceSummary.tax)}</Text>
          </Box>
          <Divider color="color-system-gray-300" />
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pb: "spacing-4"
            }}
          >
            <Text weight="medium">Invoice total:</Text>
            <Text weight="medium">
              {formatCurrency(invoicePriceSummary.netInvoice)}
            </Text>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              pb: "spacing-4"
            }}
          >
            <Text>Prompt pay discount:</Text>
            <Text>
              - {formatCurrency(invoicePriceSummary.promptPayDiscount)}
            </Text>
          </Box>
          <Divider color="color-system-gray-300" />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between"
          }}
        >
          {invoicePriceSummary.promptPayDiscount ? (
            <Text variant="h6">
              {`Total due if paid by ${formatTimestampDate(invoicePriceSummary.promptPayDueDate)}:`}
            </Text>
          ) : (
            <Text variant="h6">Amount to pay:</Text>
          )}
          <Text variant="h6">
            {formatCurrency(invoicePriceSummary.amountDue)}
          </Text>
        </Box>

        <Checkbox
          label={<CheckboxLabel />}
          value={isChecked}
          onChange={() => setIsChecked((prev) => !prev)}
        />

        <Button
          disabled={
            !(isChecked && (isPaymentMethodSelected || isPaymentFormOpen))
          }
          onClick={onPayNow}
        >
          Pay now
        </Button>
      </Box>
    </Card>
  );
};
